/**
 * IMPORTS
 */
import {t} from 'i18next';
import {getServerUrl} from 'src/aggregates/user/selectors';


/**
 * CODE
 */

/**
 * I get the file blob content and download it.
 *
 * :returns: promise with nothing
 */
async function downloadFile (fileName: string, source: string): Promise<void>
{
    // get file from server
    const response = await fetch(source);

    // get file blob content
    const blob = await response.blob();

    // create file url to download
    const fileUrl = URL.createObjectURL(blob);

    // create file link element to download
    const fileLink = document.createElement('a');

    // set file link url
    fileLink.href = fileUrl;

    // set file name with replaced caracteres
    fileLink.download = fileName?.replace(/[<>:"/\\|?*]/g,'');

    // download file
    fileLink.click();
}


/**
 * I get the file name from the metadata returned by a request.
 *
 * :param fileId: file id
 *
 * returns: promise with file name
 */
async function getDocumentName (fileId: string): Promise<string>
{
    // call function to request file metadata
    const response = await retrieveMetadata(fileId);

    // set document name fallback
    const fallback = t('Documento');

    // request failed: use fallback name
    if (response === null)
    {
        return fallback;
    }

    // request succeeded: get headers from fetched response
    const headers = response.headers;

    // 'upload-metadata' content is a string 'filename name, filetype type'
    const uploadMetadata = headers.get('upload-metadata');

    // no upload metadata header found: use fallback name
    if (uploadMetadata === null)
    {
        return fallback;
    }

    // operate on header string to get actual name of file
    const fileNameInfo = uploadMetadata?.split(',').sort()[0]?.split(' ');

    // create variable to store return value
    let fileName: string;

    // null or undefined string: use fallback value
    if (fileNameInfo === undefined)
    {
        return fallback;
    }

    // try to convert name from base64 to utf-8 if name exists
    try
    {
        (fileNameInfo.length === 2) ?
            fileName = Buffer.from(fileNameInfo[1], 'base64').toString('utf8') :
            fallback;
    }

    // cannot make conversion to utf-8: use fallback name
    catch
    {
        fileName = fallback;
    }

    // return the found name
    return fileName;
}


/**
 * I get the file name from tus by HEAD request.
 *
 * :param fileId: file id
 *
 * returns: promise with Response or null
 */
async function retrieveMetadata (fileId: string): Promise<Response | null>
{
    // generate file url
    const fileURL = getServerUrl() + '/uploads/files/' + fileId;

    // mandatory tus header for requests
    const requestHeaders = new Headers(
        {'Tus-Resumable': '1.0.0'},
    );

    // make HEAD request to tus
    const response = await fetch(
        fileURL,
        {headers: requestHeaders, method: 'HEAD'},
    ).catch((e) =>
    {
        // log the error
        console.error(e);

        // return a null promise
        return null;
    });

    // request failed or rejected: return a null promise
    if (response === null || response.ok === false)
    {
        return null;
    }

    // return fetched response
    return response;
}


/**
 * EXPORTS
 */
export
{
    downloadFile,
    getDocumentName,
    retrieveMetadata,
};
